import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { FeedbackService } from '@karve.it/core';

import { Store, createSelector } from '@ngrx/store';
import { merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { OBJECT_ICON_MAP } from 'src/app/global.constants';
import { OnlineStatusService } from 'src/app/online-status.service';
import { environment } from 'src/environments/environment';

import { SubSink } from 'subsink';


import { AppMainComponent } from '../../app.main.component';
import { AttentionItemsService } from '../../attention-items/attention-items.service';
import { ColorSchemeService } from '../../color-scheme.service';
import { JobCreateActions } from '../../job/job-create/job-create-state/job-create.actions';
import { BrandingService } from '../../services/branding.service';
import { DetailsHelperService } from '../../services/details-helper.service';

import { ActionSource } from '../../state/action-sources';
import { unsavedChangesFeature } from '../../state/unsaved-changes.reducer';

import { MenuService } from './app.menu.service';

@Component({
    selector: 'app-menu',
    templateUrl: './app.menu.component.html',
    styleUrls: ['./app.menu.component.scss'],
})
export class AppMenuComponent implements OnInit, OnDestroy {

    @Input() sidebarStatic = false;
    @Input() sidebarMobileActive = false;

    entityIcons = OBJECT_ICON_MAP;

    createJobButtonLabel: string = 'New Opportunity';

    isSideBarDisabled = false;

    currentTime: string = '';
    currentTimeNoTz: string = '';
    appVersion: string = environment.version;

    private clockInterval?: ReturnType<typeof setInterval>;

    continueEditing$ = this.store.select(createSelector(
      unsavedChangesFeature.selectQueue,
      (changes) => changes.filter((c) => c.source === ActionSource.NEW_OPPORTUNITY_V4).length > 0,
    ));

    attentionItemsTotal$ = merge(
        this.detailsHelper.getObjectUpdates(['Jobs', 'Events']),
        this.brandingService.currentZone(),
    ).pipe(
        filter((update) => Boolean(update)),
        // If zone changed, check cache first, otherwise use network as cache will be outdated
        map((update) => 'domain' in update ? 'cache-first' : 'no-cache'),
        switchMap((fetchPolicy: FetchPolicy) => this.attentionItemsService.fetchTotal(fetchPolicy)),
    );

    constructor(
        public appMain: AppMainComponent,
        public menuService: MenuService,
        public branding: BrandingService,
        public colorSchemeService: ColorSchemeService,
        private detailsHelper: DetailsHelperService,
        private brandingService: BrandingService,
        private attentionItemsService: AttentionItemsService,
        private onlineStatusService: OnlineStatusService,
        private feedbackSvc: FeedbackService,
        public store: Store,
    ) { }

    private subs = new SubSink();

    public actions = JobCreateActions;

    ngOnInit(): void {
        this.onlineStatusService.isOnline$.subscribe(status => {
            this.isSideBarDisabled = status;
        });

        this.subs.sink = this.continueEditing$.subscribe((continueEditing) => {
            this.createJobButtonLabel = continueEditing ? 'Cont. Opportunity' : 'New Opportunity';
        });

        // Requirement: Display active-zone local time, refresh it every
        // second, and clear stale values when no valid timezone exists.
        this.subs.sink = this.branding.currentBranding.subscribe((branding) => {
            const tz = branding?.timezone;
            if (this.clockInterval) { clearInterval(this.clockInterval); }
            this.clockInterval = undefined;
            if (tz) {
                this.updateClock(tz);
                this.clockInterval = setInterval(() => this.updateClock(tz), 1000);
            } else {
                this.currentTime = '';
                this.currentTimeNoTz = '';
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        if (this.clockInterval) { clearInterval(this.clockInterval); }
    }

    private updateClock(timezone: string): void {
        try {
            const now = new Date();
            const timeStr = now.toLocaleTimeString('en-US', {
                timeZone: timezone,
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            });
            this.currentTimeNoTz = timeStr;
            const offsetStr = this.getShortTimezone(timezone, now);
            this.currentTime = `${timeStr} ${offsetStr}`;
        } catch {
            this.currentTime = '';
            this.currentTimeNoTz = '';
        }
    }

    private getShortTimezone(timezone: string, date: Date): string {
        try {
            const longTZ = date.toLocaleString('en-US', { timeZone: timezone, timeZoneName: 'short' });
            const match = longTZ.match(/[A-Z]{2,4}([+-]\d+)?$/);
            if (match) { return match[0]; }
            const parts = longTZ.split(' ');
            return parts[parts.length - 1] || '';
        } catch {
            return '';
        }
    }

    reloadPage(): void {
        window.open('https://moveright.app/releases', '_blank');
        window.location.reload();
    }

    openFeedback(): void {
        this.feedbackSvc.openFeedbackDialog();
    }

    trackID(index, obj) {
        return obj.id || obj.label;
    }

}